import { useEffect } from "react";
import { useLocation } from "wouter";
import { Capacitor } from "@capacitor/core";

// Paths that stay on mobile web instead of being pushed to /install.
//
// Two groups. The first is machinery that must not be intercepted: billing
// (which bypasses IAP), legal pages the App Store links to, and the email
// verification round trip.
//
// The second is CONTENT. A link to a single item, maker, or storefront has to
// render for someone without the app or every share is a dead end: tap a
// listing from Instagram, get an App Store page for an app you have never
// heard of, and never see the thing you were shown. These pages are also the
// only discovery surface we have while the app is nowhere in App Store search,
// and gating them client-side would drop them out of Google, which renders JS.
// The gate exists to push the app *shell* (browse, cart, account), not to hide
// the catalogue.
const MOBILE_WEB_ALLOWED_PATHS = [
  "/install",
  "/upgrade",
  "/privacy",
  "/support",
  "/terms",
  "/terms-of-use",
  "/event",
  "/auth/google-callback",
  "/verify-email",
  "/check-email",
  // Content: shared links must survive without the app. "/buy" is the seller
  // storefront (App.tsx routes /buy/:handle to SellerStorefront).
  "/buy",
  "/items",
  "/u",
];

function isMobileUserAgent(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPhone|iPad|iPod|Android/i.test(ua);
}

function isAllowedPath(pathname: string): boolean {
  return MOBILE_WEB_ALLOWED_PATHS.some(
    (allowed) => pathname === allowed || pathname.startsWith(`${allowed}/`)
  );
}

/**
 * Triple-checks whether we're inside the Capacitor wrapper. The official
 * Capacitor.isNativePlatform() can briefly return false during very early
 * boot before the bridge initializes — which would cause this hook to
 * incorrectly redirect a wrapped-app user to /install. Belt and suspenders:
 * also check the `is-native` HTML class that initNativeShell sets, and
 * the global window.Capacitor object directly. Any one returning native
 * is enough.
 */
function isWrappedNative(): boolean {
  try {
    if (Capacitor.isNativePlatform()) return true;
  } catch {
    /* fallthrough */
  }
  if (typeof document !== "undefined") {
    if (document.documentElement.classList.contains("is-native")) return true;
  }
  if (typeof window !== "undefined") {
    const cap = (window as any).Capacitor;
    if (cap && typeof cap.isNativePlatform === "function") {
      try {
        if (cap.isNativePlatform()) return true;
      } catch {
        /* ignore */
      }
    }
    // Capacitor 4+ injects this property on the global object
    if (cap && cap.platform && cap.platform !== "web") return true;
  }
  return false;
}

// Re-enabled 2026-08-25. It was switched off on 2026-05-03 to test whether it
// was breaking the mobile OAuth bounce back into the wrapped app. That flow no
// longer exists: Google sign-in was moved to the native SDK (see the comment
// above startGoogleOAuthMobile in lib/native.ts), so there is no web bounce
// left to interfere with, and Apple sign-in is native too.
//
// With both apps publicly listed, a mobile browser hitting a non-content path
// now goes to /install, which forwards straight to the right store.
const MOBILE_GATE_ENABLED = true;

export function useMobileWebGate() {
  const [location] = useLocation();

  useEffect(() => {
    if (!MOBILE_GATE_ENABLED) return;
    // The Vite dev server over LAN is how the app shell gets tested on a real
    // phone. Gating it sends every device test to /install instead of the page
    // being tested. Production builds set DEV false, so the gate still stands.
    if (import.meta.env.DEV) return;

    const timer = window.setTimeout(() => {
      if (isWrappedNative()) return;
      if (!isMobileUserAgent()) return;
      if (isAllowedPath(location)) return;

      const search = window.location.search || "";
      window.location.replace(`/install${search}`);
    }, 200);

    return () => window.clearTimeout(timer);
  }, [location]);
}
